import type { CartItem } from "@/context/CartContext";
import type { ResolvedProduct } from "@/lib/resolveProductSku";
import { countryLabel } from "@/components/ui/CountryFlag";
import { PLACEHOLDER_IMAGE } from "@/types/product";

/**
 * Single source of truth for an agent-added cart item.
 *
 * The voice/text agent only decides *which* product (slug) and *how many*
 * (quantity). Every field that ends up in the cart — the part_id (`sku`), name,
 * price, variant, country, image, and the prescription/fridge flags — is
 * resolved by the frontend from canonical WooCommerce data here, exactly the
 * way the product page builds a manual add. Agent-supplied product attributes
 * are intentionally NOT used: that prevents the agent from injecting a stale or
 * invalid part_id (the "Drug Package ID is not valid" failure) and guarantees
 * agent-added and manually-added items are byte-for-byte uniform.
 *
 * The id is keyed to the variation (`var-<id>`) — identical to a manual add —
 * so adding the same variation by voice and by hand merges into one line.
 *
 * Returns null when the slug can't be resolved to a purchasable variation.
 */
export function buildResolvedCartItem(
  slug: string,
  resolved: ResolvedProduct,
): Omit<CartItem, "quantity"> | null {
  if (!resolved.sku || resolved.variationId == null) return null;

  const countryName = resolved.countryCode ? countryLabel(resolved.countryCode) : "";
  const variant = [resolved.packageLabel, countryName].filter(Boolean).join(" — ");

  return {
    id: `var-${resolved.variationId}`,
    name: resolved.name || slug,
    variant,
    price: resolved.price,
    image: resolved.image || PLACEHOLDER_IMAGE,
    variationId: resolved.variationId,
    sku: resolved.sku,
    maxQty: resolved.maxQty < 999 ? resolved.maxQty : undefined,
    tierPrices: resolved.tierPrices,
    dosageForm: resolved.dosageForm || undefined,
    isGeneric: resolved.isGeneric,
    requiresFridge: resolved.requiresFridge || undefined,
    species: resolved.species.length > 0 ? resolved.species : undefined,
    countryCode: resolved.countryCode || undefined,
    productSlug: resolved.slug || slug,
    requiresPrescription: resolved.requiresPrescription || undefined,
  };
}
